/**
 * /v1 contract additions for the R1 soft launch (all P0 features).
 * Additive only: nothing in api.ts changes shape.
 */
import { z } from 'zod';
import { BoosterKey, Snapshot } from './api.js';
import { RoundSubmissionSchema } from './round.js';

const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const Reward = z.object({
  coins: z.number().int().optional(),
  gems: z.number().int().optional(),
  boosters: z.partialRecord(BoosterKey, z.number().int()).optional(),
  rareItem: z.string().optional(),
  sticker: z.string().optional(),
});
export type Reward = z.infer<typeof Reward>;
export { Reward as RewardSchema };

// ---------- round body R1: local date (streaks, MT-06.2) + ad proofs (MO-01) ----------
export const RoundRequestR1 = RoundSubmissionSchema.extend({
  localDate: LocalDate.optional(),
  adNonces: z.object({ rescue: z.array(z.string().max(64)).max(5).optional(), doubleTips: z.string().max(64).optional() }).optional(),
});
export type RoundRequestR1 = z.infer<typeof RoundRequestR1>;

// ---------- offline-first sync (SY-01.3) ----------
export const SyncOp = z.discriminatedUnion('kind', [
  z.object({ opId: z.uuid(), kind: z.literal('round'), body: RoundRequestR1 }),
  z.object({ opId: z.uuid(), kind: z.literal('upgrade'), body: z.object({ shopId: z.string(), slot: z.string(), style: z.enum(['a', 'b', 'c']) }) }),
  /** shop decor: put an owned piece in a spot (moving it from any other spot), or clear the spot with null */
  z.object({ opId: z.uuid(), kind: z.literal('decor'), body: z.object({ shopId: z.string().max(40), spot: z.string().max(24), decor: z.string().max(64).nullable() }) }),
  z.object({ opId: z.uuid(), kind: z.literal('shopName'), body: z.object({ name: z.string().min(2).max(24) }) }),
  z.object({ opId: z.uuid(), kind: z.literal('dailyGift'), body: z.object({ localDate: LocalDate }) }),
  z.object({ opId: z.uuid(), kind: z.literal('offlineCollect'), body: z.object({ adNonce: z.string().optional() }) }),
  z.object({ opId: z.uuid(), kind: z.literal('buyBooster'), body: z.object({ booster: BoosterKey, qty: z.number().int().min(1).max(10) }) }),
  z.object({ opId: z.uuid(), kind: z.literal('settings'), body: z.record(z.string(), z.unknown()) }),
]);
export const SyncRequest = z.object({ ops: z.array(SyncOp).max(100) });
export const SyncResponse = z.object({
  results: z.array(z.object({ opId: z.string(), status: z.enum(['ok', 'duplicate', 'rejected']), reason: z.string().optional(), data: z.unknown().optional() })),
  snapshot: Snapshot,
});

// ---------- meta (MT-03, MT-05, MT-06) ----------
export const MetaState = z.object({
  dailyGift: z.object({ lastClaimDay: z.number().int().nullable(), cycleDay: z.number().int(), claimableToday: z.boolean() }),
  streak: z.object({ count: z.number().int(), lastDay: z.number().int().nullable() }),
  offline: z.object({ lastCollectAt: z.number().int(), pendingCoins: z.number().int(), ratePerHour: z.number().int() }),
  rareItems: z.record(z.string(), z.number().int()),
  stickers: z.record(z.string(), z.number().int()),
  payer: z.boolean(),
  coppa: z.boolean(),
  consentVersion: z.number().int().nullable(),
  settings: z.record(z.string(), z.unknown()),
  linked: z.array(z.enum(['email', 'apple', 'google', 'discord'])),
  /** the costume Pip wears (MT-09); absent from servers before the native-apps batch */
  costume: z.string().optional(),
  /** the look Pip wears (economy.skins, `classic` = kraft); absent from servers before migration 0010_skins */
  skin: z.string().optional(),
  /** COPPA: a parent confirmed by email (purchases allowed for an under-13 account) */
  parentVerified: z.boolean().optional(),
  /** owned decor items (`decor_*`: Rush top 3, album sets, the store) and their counts, to show and place them */
  decor: z.record(z.string(), z.number().int()).optional(),
});
export const SnapshotR1 = Snapshot.extend({ meta: MetaState });
export type SnapshotR1 = z.infer<typeof SnapshotR1>;

export const DailyGiftClaim = z.object({ localDate: LocalDate });
export const DailyGiftResponse = z.object({ giftDay: z.number().int(), reward: Reward, snapshot: SnapshotR1 });
export const OfflineCollectRequest = z.object({ adNonce: z.string().optional() });
export const OfflineCollectResponse = z.object({ coins: z.number().int(), doubled: z.boolean(), snapshot: SnapshotR1 });
export const BuyBoosterRequest = z.object({ booster: BoosterKey, qty: z.number().int().min(1).max(10), idemKey: z.string().min(8) });
export const BuyRareItemRequest = z.object({ rareItem: z.string(), idemKey: z.string().min(8) });

// ---------- links (SO-01, SO-02, SO-03, SO-05) ----------
export const LinkType = z.enum(['restock', 'daily', 'beat', 'friend']);
export const CreateRestockLink = z.object({ shopId: z.string(), rareItem: z.string() });
export const CreateDailyLink = z.object({ date: LocalDate, roundId: z.uuid() });
export const CreateBeatLink = z.object({ levelId: z.string(), roundId: z.uuid() });
export const LinkCreated = z.object({ linkId: z.string(), url: z.string(), shareText: z.string(), expiresAt: z.number().int().nullable() });

export const PublicPlayer = z.object({ id: z.uuid(), name: z.string(), shopName: z.string().nullable(), costume: z.string(), level: z.number().int() });
export const LinkResult = z.object({ moves: z.number().int(), timeMs: z.number().int(), stars: z.number().int(), grid: z.string(), result: z.enum(['win', 'fail']) });

/** GET /v1/l/:id — public, no auth; everything help mode needs in ONE request. */
export const LinkView = z.object({
  linkId: z.string(),
  type: LinkType,
  status: z.enum(['active', 'expired', 'used']),
  owner: PublicPlayer,
  level: z.unknown().nullable(), // LevelV1 JSON for restock/daily/beat
  seed: z.number().int(),
  rareItem: z.string().nullable(),
  ownerResult: LinkResult.nullable(), // daily/beat side-by-side
  reward: Reward, // what the friend gets for helping
  expiresAt: z.number().int().nullable(),
  /** signed-in viewer only: already played this daily/beat link, and their result */
  played: z.boolean().optional(),
  viewerResult: LinkResult.nullable().optional(),
  /** store buttons (SO-05.1 deferred deep link); absent when no store URL is configured */
  install: z
    .object({
      /** App Store page: copy `link` to the clipboard in the click handler first ("Paste invite" on first launch) */
      appStore: z.string().nullable(),
      /** Play Store page with `referrer=mp_link=<path?query>` (Install Referrer API) */
      playStore: z.string().nullable(),
      /** the share link the app opens after install */
      link: z.string(),
    })
    .optional(),
});
export type LinkView = z.infer<typeof LinkView>;

export const HelpStart = z.object({ attempt: z.number().int(), seed: z.number().int() });
export const HelpComplete = z.object({ round: RoundRequestR1 });
export const HelpCompleteResponse = z.object({
  delivered: z.boolean(),
  attempt: z.number().int(),
  retryAllowed: z.boolean(),
  helperReward: Reward,
  ownerResult: LinkResult.nullable(),
  you: LinkResult,
});

export const DailyView = z.object({ date: LocalDate, level: z.unknown(), seed: z.number().int(), specsVersion: z.number().int(), done: LinkResult.nullable() });
export const DailyResultRequest = z.object({ round: RoundRequestR1, localDate: LocalDate, linkId: z.string().optional() });
export const DailyResultResponse = z.object({ result: LinkResult, reward: Reward, snapshot: SnapshotR1 });
export const BeatResultRequest = z.object({ round: RoundRequestR1 });
export const BeatResultResponse = z.object({
  winner: z.enum(['you', 'owner']),
  you: LinkResult,
  ownerResult: LinkResult,
  reward: Reward,
  trophyUntil: z.number().int().nullable(),
  snapshot: SnapshotR1,
});

// ---------- inbox ----------
/** ICU params of an inbox message: plain values. */
export const InboxParams = z.record(z.string(), z.union([z.string(), z.number()]));
export const InboxItem = z.object({
  id: z.uuid(),
  kind: z.enum(['restock_delivered', 'league_reward', 'gift', 'sticker', 'purchase', 'system']),
  /** fallback text (the player's language when the server has the key, else English): for clients that do not render keys */
  title: z.string(),
  body: z.string(),
  reward: Reward,
  createdAt: z.number().int(),
  claimed: z.boolean(),
  /** i18n keys (content/i18n game.json `inbox.msg.*`) the client renders with `params` in its locale */
  titleKey: z.string().optional(),
  bodyKey: z.string().optional(),
  params: InboxParams.optional(),
  /** param name → i18n key: translate that key first and use it as the param (e.g. `tier` → `league.tier.gold`) */
  paramKeys: z.record(z.string(), z.string()).optional(),
  /** the rare item the message is about (restock delivered): sprite and shop deep link */
  rareItem: z.string().optional(),
});
export const InboxResponse = z.object({ items: z.array(InboxItem) });

// ---------- friends (SO-06) ----------
export const FriendInvite = z.object({ code: z.string(), url: z.string() });
export const FriendAccept = z.object({ code: z.string().min(4).max(32) });
export const Friend = PublicPlayer.extend({ lastActiveAt: z.number().int(), shopsDone: z.number().int(), blocked: z.boolean() });
export const FriendsResponse = z.object({ friends: z.array(Friend) });
/** `decor`: spot → decor id, for the shops that have some (a visit shows it) */
export const FriendStreet = z.object({
  player: PublicPlayer,
  shops: z.record(z.string(), z.object({ upgrades: z.record(z.string(), z.enum(['a', 'b', 'c'])), decor: z.record(z.string(), z.string()).optional() })),
  trophyUntil: z.number().int().nullable(),
});

// ---------- referral rewards: GET /v1/referrals (403 `coppa` for under-13 accounts) ----------
/** The rules as the server applies them (tunable in apps/api/src/referrals/referrals.ts). */
export const ReferralRules = z.object({
  /** the invitee wins this level, with at least this many levels won */
  milestoneLevel: z.number().int(),
  inviteeGems: z.number().int(),
  inviterGems: z.number().int(),
  /** rewarded referrals an inviter earns per rolling `windowDays` (the invitee is paid regardless) */
  maxRewarded: z.number().int(),
  windowDays: z.number().int(),
  /** an account at most this many days old can be referred */
  newAccountDays: z.number().int(),
});
export const ReferralsResponse = z.object({
  code: z.string(),
  url: z.string(),
  rules: ReferralRules,
  /** as the inviter: invitees before their milestone, invitees that paid the caller, rewards left in the current window */
  counts: z.object({ pending: z.number().int(), rewarded: z.number().int(), remaining: z.number().int() }),
  /** as the invitee: null when nobody referred the caller; `levelsWon` counts toward the milestone */
  referred: z.object({ status: z.enum(['pending', 'rewarded', 'ineligible']), levelsWon: z.number().int() }).nullable(),
});

// ---------- league (CP-01) ----------
export const LeagueEntry = z.object({ playerId: z.uuid(), name: z.string(), stars: z.number().int(), rank: z.number().int(), you: z.boolean() });
export const LeagueCurrent = z.object({
  week: z.number().int(),
  tier: z.number().int(),
  tierName: z.string(),
  endsAt: z.number().int(),
  joined: z.boolean(),
  entries: z.array(LeagueEntry),
  promoteCount: z.number().int(),
  demoteCount: z.number().int(),
});
export const LeagueResult = z.object({ week: z.number().int(), tier: z.number().int(), newTier: z.number().int(), rank: z.number().int(), outcome: z.enum(['promoted', 'held', 'demoted', 'removed']), reward: Reward });

// ---------- ads (MO-01, MO-02) ----------
/**
 * `freeGems` and `booster` (2L) pay out at /v1/ads/reward/complete, like `dailySpin`: gems, or one booster of the
 * kind named in AdStart.ref (undo, shuffle, magnet, extraSlot) that the player holds none of.
 */
export const AdSlot = z.enum(['rescue', 'doubleTips', 'fastRestock', 'dailySpin', 'doubleOffline', 'freeGems', 'booster']);
/** `ref`: the restock link id (fastRestock) or the booster key (booster). */
export const AdStart = z.object({ slot: AdSlot, ref: z.string().max(64).optional() });
export const AdStartResponse = z.object({
  nonce: z.string(),
  remainingToday: z.number().int(),
  personalized: z.boolean(),
  /** H5 web ads: signed proof to return after minWatchMs (no server-side SSV exists for H5) */
  proof: z.string().optional(),
  minWatchMs: z.number().int().optional(),
});
export const AdComplete = z.object({ nonce: z.string(), network: z.string(), proof: z.string().optional() });
export const AdCompleteResponse = z.object({ granted: Reward, snapshot: SnapshotR1 });
export const InterstitialLog = z.object({ afterLevel: z.number().int(), network: z.string(), shown: z.boolean() });

// ---------- store (MO-03, MO-06) ----------
/**
 * `boosters` (booster packs), `coins` (coin pouches) and `decor` are listed only for `?catalog=2`
 * (StoreQuery): clients before them parse `kind` with the first four and would refuse the list.
 * `priceCoins` is present on coin-priced products only.
 */
export const StoreProduct = z.object({
  id: z.string(),
  kind: z.enum(['gems', 'starter', 'bundle', 'cosmetic', 'boosters', 'coins', 'decor']),
  priceUsdCents: z.number().int().nullable(),
  priceGems: z.number().int().nullable(),
  priceCoins: z.number().int().optional(),
  contents: Reward.extend({ costume: z.string().optional(), skin: z.string().optional(), decor: z.string().optional() }),
  label: z.string().nullable(),
  offerEndsAt: z.number().int().nullable(),
  owned: z.boolean(),
});
/**
 * Two figures, because they answer different questions. `spentTodayUsdCents` is what today's
 * purchases have actually charged, and is what the "Are you sure?" shows the player. The daily
 * confirmation, though, is demanded against charges *and* `heldTodayUsdCents`, today's open
 * checkout pages (holdCheckout), exactly as the API's own guard counts them: a client that knew
 * only the charges would skip its dialog and take the API's 409 confirm_required as a block the
 * screen cannot explain.
 */
export const StoreResponse = z.object({
  products: z.array(StoreProduct),
  purchasesBlocked: z.boolean(),
  spentTodayUsdCents: z.number().int(),
  heldTodayUsdCents: z.number().int(),
  confirmOverUsdCents: z.number().int(),
});
/** GET /v1/store query: the catalog version the client understands (default 1: gems, offers and cosmetics). */
export const StoreQuery = z.object({ catalog: z.coerce.number().int().min(1).max(100).default(1) });
/** POST /v1/store/spend: buy any product priced in gems or coins (answers the snapshot, like the other gem spends). */
export const SpendRequest = z.object({ productId: z.string().min(1).max(64), idemKey: z.string().min(8).max(64) });
export const PurchaseProvider = z.enum(['dev', 'stripe', 'apple', 'google']);
export const PurchaseVerify = z.object({ provider: PurchaseProvider, productId: z.string(), receipt: z.string().max(20000), idemKey: z.string().min(8), confirm: z.boolean().optional() });
export const DevPurchase = z.object({ productId: z.string(), idemKey: z.string().min(8), confirm: z.boolean().optional() });
export const BuyCosmetic = z.object({ productId: z.string(), idemKey: z.string().min(8) });
export const CostumeRequest = z.object({ costume: z.string().regex(/^[a-z_]{1,32}$/) });
export const SkinRequest = z.object({ skin: z.string().regex(/^[a-z_]{1,32}$/) });
export const PurchaseResponse = z.object({
  status: z.enum(['granted', 'pending', 'failed', 'blocked']),
  reason: z.string().optional(),
  granted: Reward.extend({ costume: z.string().optional(), skin: z.string().optional() }),
  snapshot: SnapshotR1,
});
export const CheckoutRequest = z.object({ productId: z.string(), successUrl: z.string().url(), cancelUrl: z.string().url(), idemKey: z.string().min(8).optional(), confirm: z.boolean().optional() });

// ---------- accounts (SY-01) ----------
export const EmailStart = z.object({ email: z.email() });
export const EmailVerify = z.object({ token: z.string().min(16) });
/**
 * An OIDC id token from Apple or Google (web popup or the native SDK sheet). `nonce`: the raw
 * value from GET /v1/auth/nonce when the client passed it (or its SHA-256 hex) to the SDK;
 * the token's `nonce` claim must then match it, and each nonce works once.
 * Apple only: `authorizationCode` of the same sign-in (the iOS sheet's `authorizationCode`,
 * Sign in with Apple JS's `authorization.code`), which the API exchanges for the refresh token
 * that unlink and account deletion revoke; `redirectUri`, on the web, the return URL the
 * authorization used (Apple checks it again on the exchange).
 */
export const IdTokenLink = z.object({
  idToken: z.string().min(10),
  nonce: z.string().min(16).max(128).optional(),
  authorizationCode: z.string().min(8).max(1024).optional(),
  redirectUri: z.url({ protocol: /^https?$/ }).max(512).optional(),
});
/** 200 body of DELETE /v1/auth/link/:provider (409 last_sign_in_method while it is the account's only sign-in). */
export const UnlinkedResponse = z.object({ unlinked: z.literal(true), snapshot: SnapshotR1 });
export const AuthNonce = z.object({ nonce: z.string(), expiresAt: z.number().int() });
/** `deleting`: that account is scheduled for deletion; keeping it answers 410 account_deleting (keep the other one). */
const LinkSide = z.object({ level: z.number().int(), stars: z.number().int(), deleting: z.boolean().optional() });
export const LinkConflict = z.object({ conflict: z.literal(true), current: LinkSide, other: LinkSide, resolveToken: z.string() });
export const LinkResolve = z.object({ resolveToken: z.string(), keep: z.enum(['current', 'other']) });
/** 200 body of /v1/auth/email/verify and /v1/auth/link/:provider (409 carries LinkConflict). */
export const LinkedResponse = z.object({ linked: z.literal(true), snapshot: SnapshotR1 });
export const TransferToken = z.object({ token: z.string(), url: z.string(), expiresAt: z.number().int() });
/**
 * `confirm: true` is required (review #2 LNK-1): the app shows the account from
 * GET /v1/transfer/:token/preview and claims only after the player confirms.
 */
export const TransferClaim = z.object({ token: z.string().min(8), deviceId: z.string().min(8), confirm: z.boolean().optional() });
/** GET /v1/transfer/:token/preview: which account a transfer link opens, without spending it. */
export const TransferPreview = z.object({ level: z.number().int(), shopName: z.string().nullable(), playerTag: z.string(), expiresAt: z.number().int() });

// ---------- settings, privacy, parental (SY-02) ----------
export const Settings = z.object({
  sound: z.boolean(),
  music: z.boolean(),
  vibration: z.boolean(),
  language: z.string(),
  notifications: z.record(z.string(), z.boolean()),
  colorBlind: z.boolean(),
  /** Clearer items: lookalike clutter drawn with its level's substitute art; follows the account */
  clearerItems: z.boolean(),
  reducedMotion: z.boolean(),
  /** City outfits: Pip wears each city's own outfit while he wears no wardrobe outfit; follows the account */
  cityOutfits: z.boolean(),
}).partial();
export const AgeRequest = z.object({ birthYear: z.number().int().min(1900).max(2100) });
export const ConsentRequest = z.object({ version: z.number().int(), analytics: z.boolean(), ads: z.boolean(), personalizedAds: z.boolean(), region: z.enum(['eu', 'uk', 'us-ca', 'other']) });
export const DeleteAccountResponse = z.object({ scheduledFor: z.number().int() });
